<template>
    <div class="is-box is-large">
        <div v-if="hasError" class="has-text-danger">
            <e-icon icon="exclamation-triangle" />
            {{ errorMessage }}
        </div>
        <video
            v-else-if="playerLoaded"
            ref="videoPlayer"
            class="video-js"
            :data-setup="videoSetup"
            :crossorigin="usesCredentials ? 'use-credentials' : 'anonymous'"
        >
            <track
                v-if="closedCaptions !== ''"
                kind="subtitles"
                :src="closedCaptions !== '' ? encodedClosedCaptions : undefined"
                label="English"
            />

            <track v-else kind="captions" src="" label="No captions available" default />
        </video>
    </div>
</template>

<script>
import Cookies from 'js-cookie';

import { initialState, rootDomain } from '@/Helpers.js';
import LoadExternalScriptsMixin from '@/mixins/LoadExternalScripts.mixin.js';
import Video from '@/models/Video.js';

export default {
    name: 'VideoPlayer',

    mixins: [LoadExternalScriptsMixin],

    props: {
        videoId: {
            type: [String, Number],
            required: true,
        },
        isVideoAutoPlay: {
            type: Boolean,
            required: false,
            default: false,
        },
        closedCaptions: {
            type: String,
            required: false,
            default: '',
        },
    },
    emits: ['dimensions-changed', 'time-update'],

    data() {
        return {
            player: null,
            playerLoaded: false,
            options: null,
            scriptTag: null,
            hasError: false,
            errorMessage: '',
        };
    },

    computed: {
        debugMode() {
            return initialState('Debug');
        },

        video() {
            const video = Video.find(this.videoId);

            return video || this.$store.getters['Videos/get'](this.videoId);
        },

        encodedClosedCaptions() {
            return URL.createObjectURL(new Blob([this.closedCaptions], { type: 'text/plain' }));
        },

        videoSetup() {
            return JSON.stringify({ fluid: true });
        },

        /** CloudFront cookie auth needs credentials; S3 signed URLs do not. */
        usesCredentials() {
            const cookies = this.video?.hls_cookies;

            return Boolean(cookies && Object.keys(cookies).length > 0);
        },
    },

    created() {
        // Use only resolved_hls_url from dynamic URL resolver
        const hlsUrl = this.video.resolved_hls_url;

        if (!hlsUrl) {
            this.hasError = true;
            this.errorMessage = 'Video URL is not available. The video may still be processing.';
            return;
        }

        this.options = {
            autoplay: this.isVideoAutoPlay,
            controls: true,
            sources: [
                {
                    src: hlsUrl,
                    type: 'application/x-mpegURL',
                    withCredentials: this.usesCredentials,
                },
            ],
        };
    },

    mounted() {
        if (!this.hasError) {
            this.loadVideoAssets();
        }
    },

    beforeUnmount() {
        if (this.player) {
            this.player.dispose();
        }
    },

    methods: {
        /** Loads the stylesheet, script, cookies, and initializes the player. */
        loadVideoAssets() {
            // Load the Video.js styles & scripts
            this.loadStylesheet('https://vjs.zencdn.net/7.20.1/video-js.css');
            this.loadScript('https://vjs.zencdn.net/7.20.1/video.min.js', 'video-player', () => this.loadPlayer());

            // Set any CloudFront cookies required for HLS auth
            Object.entries(this.video.hls_cookies || []).forEach(([key, value]) => {
                Cookies.set(key, value, { domain: rootDomain(initialState('AppHost')) });
            });
        },

        /** Actually initialize the Video.js player once the script is loaded. */
        loadPlayer() {
            this.playerLoaded = true;

            this.$nextTick(() => {
                this.player = window.videojs(this.$refs.videoPlayer, this.options);

                this.announceDimensions(this.player.currentDimensions());

                this.player.on('timeupdate', () => {
                    this.$emit('time-update', this.player.currentTime());
                });

                this.player.on('playerresize', () => {
                    this.announceDimensions(this.player.currentDimensions());
                });
            });
        },

        announceDimensions({ width, height }) {
            this.$emit('dimensions-changed', { width, height });
        },
    },
};
</script>
